import React from 'react';
import { motion } from 'framer-motion';
import { Package, MapPin, Ship, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import ContainerTrackingComponent from '../components/dashboard/ContainerTracking';
import ShipmentMapComponent from '../components/dashboard/ShipmentMap';

const ContainerTrackingPage = () => {
  const trackingMetrics = [
    { label: 'In Transit', value: '156', color: 'from-blue-500 to-blue-600', icon: Ship },
    { label: 'At Port', value: '42', color: 'from-orange-500 to-orange-600', icon: MapPin },
    { label: 'Delivered', value: '891', color: 'from-green-500 to-green-600', icon: CheckCircle2 },
    { label: 'Delayed', value: '8', color: 'from-red-500 to-red-600', icon: AlertCircle },
  ];

  const containerStatus = [
    { 
      container: 'MSCU1234567', 
      vessel: 'MSC MAYA', 
      status: 'In Transit', 
      location: 'Pacific Ocean',
      eta: '2025-11-05',
      progress: 65
    },
    { 
      container: 'MAEU9876543', 
      vessel: 'MAERSK ESSEX', 
      status: 'At Port', 
      location: 'Los Angeles',
      eta: '2025-11-02',
      progress: 95
    },
    { 
      container: 'COSCO456789', 
      vessel: 'COSCO GLORY', 
      status: 'In Transit', 
      location: 'Indian Ocean',
      eta: '2025-11-08',
      progress: 45
    },
    { 
      container: 'OOLU234567', 
      vessel: 'OOCL TOKYO', 
      status: 'Delivered', 
      location: 'Seattle',
      eta: '2025-10-28',
      progress: 100
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0072FF] via-[#00C6FB] to-[#0A34A0] rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center space-x-3 mb-3">
          <Package className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Container Tracking</h1>
        </div>
        <p className="text-white/90 text-lg">
          Real-time container tracking and shipment visibility across global trade lanes
        </p>
      </div>

      {/* Tracking Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {trackingMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon className="w-5 h-5" />
                <h3 className="text-sm font-medium opacity-80">{metric.label}</h3>
              </div>
              <p className="text-3xl font-bold">{metric.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Shipment Map */}
      <ShipmentMapComponent />

      {/* Container Tracking Component */}
      <ContainerTrackingComponent />

      {/* Live Container Status */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
      >
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center">
          <Ship className="w-5 h-5 mr-2 text-[#0072FF]" />
          Live Container Status
        </h3>
        <div className="space-y-4">
          {containerStatus.map((container, index) => (
            <div
              key={index}
              className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    container.status === 'Delivered' ? 'bg-green-100 dark:bg-green-900/30' :
                    container.status === 'At Port' ? 'bg-orange-100 dark:bg-orange-900/30' :
                    'bg-blue-100 dark:bg-blue-900/30'
                  }`}>
                    {container.status === 'Delivered' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : container.status === 'At Port' ? (
                      <MapPin className="w-5 h-5 text-orange-600" />
                    ) : (
                      <Ship className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">{container.container}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{container.vessel}</div>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    container.status === 'Delivered' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                    container.status === 'At Port' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  }`}>
                    {container.status}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Current Location</div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white">{container.location}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">ETA</div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white">{container.eta}</div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-600 dark:text-slate-400">Progress</span>
                  <span className="text-xs font-medium text-slate-900 dark:text-white">{container.progress}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      container.progress === 100 ? 'bg-green-500' :
                      container.progress >= 90 ? 'bg-orange-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${container.progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContainerTrackingPage;
